import type { Usage } from '@lyrmo/shared';
import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../../api/api-error';
import { useI18n } from '../../i18n/i18n-context';
import { useAccount } from './account-context';

/** A choice tried on the sample: at work, its result, or why there's none. */
export type Trial =
  | { readonly status: 'running' }
  | { readonly status: 'done'; readonly text: string }
  | { readonly status: 'failed'; readonly message: string };

/** What trying a choice gets back: the sample as it makes it, and the day's usage after. */
export interface TrialResult {
  readonly text: string;
  readonly usage: Usage;
}

/**
 * Tries choices on the sample, keeping the last result of each, until it's tried again or
 * forgotten, as when the choice changes. Closing the dialog stops any try at work; a stopped
 * try costs nothing.
 */
export function useTrials() {
  const { describeError } = useI18n();
  const { applyUsage } = useAccount();
  const [trials, setTrials] = useState<Readonly<Record<string, Trial>>>({});
  const runs = useRef(new Map<string, AbortController>());

  useEffect(() => {
    const active = runs.current;
    return () => {
      for (const controller of active.values()) controller.abort();
    };
  }, []);

  const settle = (id: string, trial: Trial | null) => {
    setTrials((current) => {
      const rest = Object.fromEntries(Object.entries(current).filter(([other]) => other !== id));
      return trial ? { ...rest, [id]: trial } : rest;
    });
  };

  async function tryIt(id: string, run: (signal: AbortSignal) => Promise<TrialResult>) {
    runs.current.get(id)?.abort();
    const controller = new AbortController();
    runs.current.set(id, controller);
    settle(id, { status: 'running' });
    try {
      const result = await run(controller.signal);
      applyUsage(result.usage);
      settle(id, { status: 'done', text: result.text });
    } catch (caught) {
      if (controller.signal.aborted) return;
      if (caught instanceof ApiError && caught.usage) applyUsage(caught.usage);
      settle(id, { status: 'failed', message: describeError(caught) });
    } finally {
      if (runs.current.get(id) === controller) runs.current.delete(id);
    }
  }

  return {
    trials,
    tryIt,
    forget: (id: string) => {
      settle(id, null);
    },
  };
}
